import { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Boxes, IndianRupee, Wallet, Landmark, Settings, LogOut, Menu, X } from "lucide-react";
import Logo from "./Logo";
import SessionBanner from "./SessionBanner";
import { SidebarNav } from "./DashboardLayout";
import { useAuth } from "../context/AuthContext";
import api, { inr } from "../lib/api";
import Kyc from "../pages/app/Kyc";

const SECTIONS = [
  { title: "", items: [{ to: "/franchise", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    title: "NETWORK", items: [
      { to: "/franchise/customers", label: "My Customers", icon: Users },
      { to: "/franchise/shipments", label: "Shipments", icon: Boxes },
    ],
  },
  {
    title: "EARNINGS", items: [
      { to: "/franchise/earnings", label: "Earnings", icon: IndianRupee },
      { to: "/franchise/wallet", label: "Wallet & Payouts", icon: Wallet },
      { to: "/franchise/bank", label: "Bank Account", icon: Landmark },
    ],
  },
  { title: "", items: [{ to: "/franchise/settings", label: "Settings", icon: Settings }] },
];

export default function FranchiseLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const kycOk = user?.kyc_status === "approved";

  useEffect(() => {
    if (user?.role === "franchise" && kycOk) {
      api.get("/franchise/wallet").then(({ data }) => setWallet(data.wallet)).catch(() => {});
    }
  }, [user?.role, kycOk]);

  if (user && user.role !== "franchise") {
    return <Navigate to="/login" replace />;
  }

  const doLogout = async () => {
    await logout();
    window.location.href = "/franchise-login";
  };

  return (
    <div className="flex min-h-screen bg-shipany-mist" data-testid="franchise-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-shipany-navy lg:flex">
        <div className="px-5 py-5">
          <Logo dark />
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#FF9933]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#FF9933]">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#FF9933]" /> Franchise Partner
          </span>
        </div>
        <SidebarNav sections={SECTIONS} />
        <div className="border-t border-white/10 p-4">
          <button onClick={doLogout} data-testid="franchise-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-shipany-navy">
            <div className="flex items-center justify-between px-5 py-5">
              <Logo dark />
              <button onClick={() => setMobileOpen(false)} className="text-slate-400" data-testid="franchise-sidebar-close-btn"><X className="h-5 w-5" /></button>
            </div>
            <SidebarNav sections={SECTIONS} onNavigate={() => setMobileOpen(false)} />
            <div className="border-t border-white/10 p-4">
              <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:text-white">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <SessionBanner />
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/85 px-5 py-3.5 backdrop-blur-xl">
          <button className="lg:hidden text-shipany-navy" onClick={() => setMobileOpen(true)} data-testid="franchise-mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            {kycOk && (
              <button onClick={() => navigate("/franchise/wallet")} data-testid="franchise-topbar-balance"
                className="mono rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-100">
                {inr(wallet?.available ?? 0)} <span className="text-[10px] font-semibold text-emerald-500">EARNED</span>
              </button>
            )}
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FF9933] text-xs font-bold text-shipany-navy">{(user?.name || "F")[0].toUpperCase()}</span>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-shipany-navy" data-testid="franchise-topbar-user-name">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-5 sm:p-8">
          {kycOk ? <Outlet /> : (
            <div data-testid="franchise-kyc-gate">
              <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                <p className="text-sm font-bold text-amber-800">Complete KYC to activate your franchise</p>
                <p className="mt-1 text-xs text-amber-700">Your franchise activates after ShipAny admin approves your KYC. Customer onboarding and withdrawals unlock after activation.</p>
              </div>
              <Kyc />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
